import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Popover, Tooltip} from "antd";
import {CloseOutlined, ReloadOutlined, StopOutlined} from "@ant-design/icons";
import {useGlobalContext, useUserContext} from "../../hooks/globalContext";
import "../table/orderActions.css";
import type {YandexCancellation} from "../../models/requests/yandexSettings";

const statuses: Record<string, string> = {
    new: "Заявка создана", estimating: "Расчёт доставки", estimating_failed: "Не удалось рассчитать доставку",
    ready_for_approval: "Курьер ещё не вызван", accepted: "Заявка принята", performer_lookup: "Поиск курьера",
    performer_draft: "Поиск курьера", performer_found: "Курьер назначен", performer_not_found: "Курьер не найден",
    pickup_arrived: "Курьер прибыл", ready_for_pickup_confirmation: "Ожидает передачи заказа",
    pickuped: "Заказ у курьера", delivery_arrived: "Курьер у получателя",
    ready_for_delivery_confirmation: "Ожидает вручения", delivered: "Доставлено", delivered_finish: "Доставлено",
    returning: "Возврат заказа", return_arrived: "Курьер привёз возврат", ready_for_return_confirmation: "Ожидает приёмки возврата",
    returned: "Заказ возвращён", returned_finish: "Заказ возвращён", failed: "Ошибка доставки",
    cancelled: "Доставка отменена", cancelled_with_payment: "Доставка отменена платно",
    cancelled_by_taxi: "Доставка отменена Яндексом", cancelled_with_items_on_hands: "Отмена с заказом у курьера — свяжитесь с Яндексом",
};

export function YandexDeliveryControls({orderId, number, dispatchInfo}: {orderId: string; number?: string; dispatchInfo?: {state:string;reason:string}}) {
    const {api} = useGlobalContext();
    const {currentMenuId} = useUserContext();
    const [info, setInfo] = useState<YandexCancellation | null>(null);
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const controller = useRef<AbortController | null>(null);
    const confirmation = useRef<HTMLDivElement>(null);
    const cancelButton = useRef<HTMLButtonElement>(null);
    const refreshButton = useRef<HTMLButtonElement>(null);
    const busy = useRef(false);

    useEffect(() => {
        setInfo(null);
        setError("");
        setNotice("");
        setConfirming(false);
        setLoading(false);
        busy.current = false;
        return () => controller.current?.abort();
    }, [api, currentMenuId, orderId]);

    useEffect(() => {
        if (dispatchInfo) setInfo(current => current ? {...current,state:dispatchInfo.state,reason:dispatchInfo.reason} : current);
    },[dispatchInfo?.state,dispatchInfo?.reason]);

    async function refreshStatus() {
        if (busy.current) return null;
        busy.current = true;
        const request = new AbortController();
        controller.current = request;
        setError("");
        setNotice("");
        setLoading(true);
        try {
            const result = await api.getYandexCancellation({controller: request, setLoading: () => {}}, currentMenuId, orderId);
            if (request.signal.aborted) return null;
            setInfo(result);
            return result;
        } catch {
            if (!request.signal.aborted) {
                setInfo(null);
                setError("Не удалось получить статус доставки. Повторите обновление или проверьте заявку в кабинете Яндекса.");
            }
            return null;
        } finally {
            if (!request.signal.aborted) { busy.current = false; setLoading(false); }
        }
    }

    async function prepareCancellation() {
        const fresh = await refreshStatus();
        if (fresh && ["stop", "free", "paid"].includes(fresh.action)) setConfirming(true);
        else if (fresh?.action === "cancelled") {
            setNotice("Заявка уже отменена в Яндексе. Статус обновлён; запрос на отмену не отправлялся.");
            requestAnimationFrame(() => refreshButton.current?.focus());
        } else if (fresh?.action === "stopped") {
            setNotice("Вызов курьера уже отключён. Заявку можно проверить в кабинете Яндекса.");
            requestAnimationFrame(() => refreshButton.current?.focus());
        }
    }

    function dismissConfirmation() {
        if (busy.current) return;
        setConfirming(false);
        requestAnimationFrame(() => cancelButton.current?.focus());
    }

    async function cancelDelivery() {
        if (!info || busy.current || (info.action === "paid" && !validPaidPrice)) return;
        busy.current = true;
        setLoading(true);
        setError("");
        setNotice("");
        const request = new AbortController();
        controller.current?.abort();
        controller.current = request;
        try {
            const result = await api.cancelYandexDelivery({controller: request, setLoading: () => {}}, currentMenuId, orderId, info);
            if (!request.signal.aborted) setInfo(result);
        } catch {
            if (!request.signal.aborted) {
                setInfo(null);
                setError("Отмена не подтверждена. Обновите статус: условия могли измениться или Яндекс мог обработать запрос без ответа. Если статус не обновляется, проверьте заявку в кабинете Яндекса.");
            }
        } finally {
            if (!request.signal.aborted) {
                busy.current = false;
                setLoading(false);
                setConfirming(false);
                requestAnimationFrame(() => refreshButton.current?.focus());
            }
        }
    }

    const dispatchState = info?.state || dispatchInfo?.state;
    const dispatchReason = info?.reason || dispatchInfo?.reason;
    const terminal = dispatchState === "cancelled" || dispatchState === "delivered" || dispatchState === "returned" || info && ["delivered","delivered_finish","returned","returned_finish","cancelled","cancelled_with_payment","cancelled_by_taxi"].includes(info.status);
    const validPaidPrice = info?.price !== "" && Number.isFinite(Number(info?.price)) && Number(info?.price) >= 0 && Boolean(info?.currency);
    const canCancel = !info || ["stop", "free", "paid"].includes(info.action);
    const cancelLabel = info?.action === "stop" ? "Отключить вызов курьера Яндекс" : "Отменить доставку Яндекс";
    const refreshLabel = "Обновить статус доставки Яндекс";
    const price = info?.price && Number.isFinite(Number(info.price))
        ? `${new Intl.NumberFormat("ru-RU", {maximumFractionDigits: 4}).format(Number(info.price))} ${info.currency}`
        : "сумму уточните в кабинете Яндекса";

    return <div role="group" className="yandex-delivery" aria-label="Управление доставкой Яндекс" aria-busy={loading}>
        <div className="order-actions__row">
            <span className="order-actions__label">Яндекс</span>
            <div className="order-actions__buttons">
                <Tooltip title={refreshLabel} trigger={["hover", "focus"]}>
                    <Button ref={refreshButton} className="order-action-button" type="text" icon={<ReloadOutlined/>}
                            aria-label={refreshLabel} loading={loading} disabled={confirming} onClick={refreshStatus}/>
                </Tooltip>
                <Popover open={confirming} trigger="click" placement="bottom" autoAdjustOverflow
                         overlayClassName="yandex-cancel-popover" destroyTooltipOnHide
                         getPopupContainer={node => node.closest<HTMLElement>(".modal-content") || document.body}
                         onOpenChange={open => { if (!open) dismissConfirmation(); }}
                         afterOpenChange={open => { if (open) confirmation.current?.focus(); }}
                         content={info && <div ref={confirmation} tabIndex={-1} className="yandex-delivery__confirmation"
                             role="dialog" aria-modal="true" aria-label="Подтверждение отмены доставки"
                             onKeyDown={event => {
                                 if (event.key === "Escape") { event.stopPropagation(); dismissConfirmation(); }
                                 if (event.key === "Tab") {
                                     const buttons = confirmation.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
                                     if (!buttons?.length) { event.preventDefault(); return; }
                                     const first = buttons[0], last = buttons[buttons.length-1];
                                     if (event.shiftKey && (document.activeElement === first || document.activeElement === confirmation.current)) { event.preventDefault(); last.focus(); }
                                     else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
                                 }
                             }}>
                             <div className="yandex-cancel-heading">
                                 <strong>{info.action === "stop" ? "Отключить вызов курьера?" : "Отменить доставку?"}</strong>
                                 <Button type="text" icon={<CloseOutlined/>} aria-label="Закрыть подтверждение" onClick={dismissConfirmation} disabled={loading}/>
                             </div>
                             {number && <p className="yandex-cancel-order">Заказ № {number}</p>}
                             <p>{info.action === "stop" ? "Kami не вызовет курьера. Заявка останется в Яндексе; её можно отменить в кабинете."
                                 : info.action === "paid" ? `Яндекс указывает платную отмену: ${price}.`
                                 : "Яндекс указывает бесплатную отмену."}</p>
                             <p>Заказ ресторана и оплата не изменятся.</p>
                             <div className="yandex-cancel-actions">
                                 <Button danger type="primary" loading={loading} disabled={info.action === "paid" && !validPaidPrice} onClick={cancelDelivery}>
                                     {info.action === "stop" ? "Отключить вызов курьера" : info.action === "paid" ? "Подтвердить платную отмену" : "Подтвердить отмену доставки"}
                                 </Button>
                                 <Button onClick={dismissConfirmation} disabled={loading}>Оставить доставку</Button>
                             </div>
                         </div>}>
                    <span className="order-action-slot">
                        <Tooltip title={canCancel ? cancelLabel : "Отмена доставки недоступна в текущем статусе"}
                                 open={confirming ? false : undefined} trigger={["hover", "focus"]}>
                            <Button ref={cancelButton} className="order-action-button" type="text" icon={<StopOutlined/>}
                                    aria-label={cancelLabel} aria-expanded={confirming}
                                    disabled={!canCancel || loading || confirming} onClick={prepareCancellation}/>
                        </Tooltip>
                    </span>
                </Popover>
            </div>
        </div>
        <div className="yandex-delivery__status" role="status" title={info ? `Заявка: ${info.claimId}` : undefined}>
            {info ? (info.state === "stopped" && ["stop", "stopped"].includes(info.action) ? "Вызов курьера отключён" : statuses[info.status] || `Статус Яндекса: ${info.status}`) : dispatchState === "cancelled" ? "Доставка отменена" : dispatchState === "delivered" ? "Доставка завершена" : dispatchState === "returned" ? "Заказ возвращён" : loading ? "Проверяем статус…" : "Статус не проверен"}
        </div>
        {!terminal && dispatchState === "manual" && <p>Автоматический вызов курьера остановлен. Проверьте оплату и доставку.</p>}
        {!terminal && dispatchState === "pending" && <p>Доставка ожидает обработки Kami.</p>}
        {!terminal && dispatchState === "accepting" && <p>Проверяем результат вызова курьера. Не создавайте вторую заявку.</p>}
        {!terminal && ["manual","stopped"].includes(dispatchState || "") && dispatchReason && <p className="yandex-delivery__notice">{/[А-Яа-я]/.test(dispatchReason) ? dispatchReason : "Автоматическая доставка остановлена. Проверьте оплату и текущую заявку в кабинете Яндекса."}</p>}
        {notice && <p className="yandex-delivery__notice" role="status">{notice}</p>}
        {error && <Alert type="error" showIcon message={error} role="alert"/>}
        {info?.state === "cancelling" && info.action !== "cancelled" && <p>Отмена ещё не подтверждена. Обновите статус или проверьте заявку в кабинете Яндекса.</p>}
        {info?.action === "unavailable" && <p>Отмена через Kami недоступна. Проверьте заявку в кабинете Яндекса или обратитесь в поддержку Яндекса.</p>}

    </div>;
}
